import { NavLink } from 'react-router-dom';
import { Users, Building2, UserCheck, LogOut, Settings, Handshake, BookOpen, X, Euro, Target, Bot, CheckSquare, UserPlus, Calendar, ChevronDown, ChevronRight, LayoutDashboard, BarChart3, Database, TrendingUp, Sparkles, Eye, Mail, Search, Zap, Coins, Crosshair, Gem, Link2, Infinity } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../lib/utils';
import { logout, supabase } from '../lib/supabase';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContextSupabase';
import { useState, useEffect } from 'react';

const navigationSections = [
  {
    title: 'DÉCOUVERTE',
    items: [
      { name: 'Discover', path: '/app/discover', icon: TrendingUp },
      { name: 'Matching IA', path: '/app/matching-ia', icon: Zap, moduleKey: 'matching_ia' },
      { name: 'Market Watch', path: '/app/market-watch', icon: Eye },
      { name: 'Pépites', path: '/app/pepites', icon: Gem, hosniOnly: true },
      { name: 'Scoot', path: '/app/scoot', icon: Search, moduleKey: 'scoot', swipeUppOnly: true },
      { name: 'Recrutement', path: '/app/recruitment', icon: UserPlus },
    ]
  },
  {
    title: 'INFLUENCEURS',
    items: [
      { name: 'Créateurs', path: '/app/influencers', icon: UserCheck, moduleKey: 'influencers' },
      { name: 'Enrichissement Apify', path: '/app/apify-integration', icon: Database, superAdminOnly: true },
    ]
  },
  {
    title: 'CAMPAGNES',
    items: [
      { name: 'Collaborations', path: '/app/collaborations', icon: Handshake, moduleKey: 'collaborations' },
      { name: 'Casting', path: '/app/casting', icon: Target, moduleKey: 'casting' },
      { name: 'Casting interne', path: '/app/casting-interne', icon: Link2, moduleKey: 'casting_interne' },
    ]
  },
  {
    title: 'CLIENTS',
    items: [
      { name: 'Clients', path: '/app/clients', icon: Building2, moduleKey: 'clients' },
    ]
  },
  {
    title: 'TÂCHES',
    items: [
      { name: 'Mes tâches', path: '/app/todo', icon: CheckSquare, moduleKey: 'todo' },
      { name: 'Tâches influenceurs', path: '/app/daily-brief', icon: Calendar, moduleKey: 'daily_brief' },
    ]
  },
  {
    title: 'OUTILS',
    items: [
      { name: 'Comptabilité', path: '/app/accounting', icon: Euro, moduleKey: 'accounting' },
      { name: 'Ressources', path: '/app/resources', icon: BookOpen, moduleKey: 'resources' },
      { name: 'Prospection', path: '/app/prospection', icon: Crosshair, moduleKey: 'prospection' },
      { name: 'Talent Manager', path: '/app/talent-manager', icon: Mail, swipeUppOnly: true },
      { name: 'Assistant IA', path: '/app/assistant', icon: Bot, superAdminOnly: true },
      { name: 'Gestion utilisateurs', path: '/app/user-approval', icon: UserCheck, superAdminOnly: true }
    ]
  }
];

interface SidebarProps {
  onClose?: () => void;
  adminOnly?: boolean;
  permission?: string;
}

export default function Sidebar({ onClose }: SidebarProps) {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { currentUser, userProfile, isAdmin } = useAuth();

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    'DÉCOUVERTE': true,
    'INFLUENCEURS': true,
    'CAMPAGNES': true,
    'CLIENTS': true,
    'TÂCHES': false,
    'OUTILS': false,
  });

  const [creditsBalance, setCreditsBalance] = useState<number>(0);
  const [isLoadingCredits, setIsLoadingCredits] = useState(true);

  useEffect(() => {
    if (userProfile?.agency_id) {
      loadCredits();
    }
  }, [userProfile?.agency_id]);

  const loadCredits = async () => {
    if (!userProfile?.agency_id) return;

    try {
      const { data, error } = await supabase.rpc('get_organization_credits', {
        org_id: userProfile.agency_id,
      });

      if (error) throw error;

      if (data && data.length > 0) {
        setCreditsBalance(data[0].balance);
      }
    } catch (error) {
      console.error('Error loading credits:', error);
    } finally {
      setIsLoadingCredits(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const toggleSection = (sectionTitle: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionTitle]: !prev[sectionTitle]
    }));
  };

  const isSuperAdmin = currentUser?.email === 'hosni@swipe-upp.com';
  const isSwipeUppTeam = currentUser?.email?.endsWith('@swipe-upp.com');
  const userPermissions = userProfile?.permissions || {};

  return (
    <div className="flex flex-col h-full" style={{ background: '#111111' }}>
      {/* Header logo */}
      <div className="h-14 flex items-center px-5 border-b border-white/[0.06]">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-white/10 rounded-xl flex items-center justify-center">
            <img
              src="https://i.postimg.cc/jq4qbh4Y/logo-campaiyn.png"
              alt="Campaly"
              className="w-5 h-5 object-contain brightness-0 invert"
            />
          </div>
          <h1 className="text-base font-bold text-white">Campaly</h1>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-5 overflow-y-auto">
        <div className="space-y-5">
          {navigationSections.map((section) => {
            const filteredItems = section.items.filter(item => {
              if (item.superAdminOnly && !isSuperAdmin) return false;
              if ((item as any).hosniOnly && !isSuperAdmin) return false;
              if (item.swipeUppOnly && !isSwipeUppTeam) return false;
              if (!item.moduleKey) return true;
              if (isSuperAdmin || userProfile?.user_role === 'admin') return true;
              return userPermissions[item.moduleKey] === true;
            });

            if (filteredItems.length === 0) return null;

            const isExpanded = expandedSections[section.title];

            return (
              <div key={section.title}>
                <button
                  onClick={() => toggleSection(section.title)}
                  className="flex items-center justify-between w-full px-2 py-1.5 text-[10px] font-semibold text-white/30 hover:text-white/50 transition-colors tracking-widest uppercase"
                >
                  <span>{section.title}</span>
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isExpanded ? 'rotate-0' : '-rotate-90'}`} />
                </button>

                {isExpanded && (
                  <div className="mt-1 space-y-0.5">
                    {filteredItems.map((item) => {
                      const Icon = item.icon;
                      return (
                        <NavLink
                          key={item.path}
                          to={item.path}
                          onClick={onClose}
                          className={({ isActive }) => cn(
                            'flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all group text-sm',
                            isActive
                              ? 'bg-white text-black font-semibold'
                              : 'text-white/50 hover:text-white hover:bg-white/[0.07]'
                          )}
                        >
                          {({ isActive }) => (
                            <>
                              <Icon className={cn('w-4 h-4 flex-shrink-0', isActive ? 'text-black' : 'text-white/40 group-hover:text-white/70')} />
                              <span className="font-medium">{item.name}</span>
                            </>
                          )}
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Dashboard */}
        {(isSuperAdmin || userProfile?.user_role === 'admin' || userPermissions.dashboard) && (
          <div className="mt-5 pt-5 border-t border-white/[0.06]">
            <NavLink
              to="/app/dashboard"
              onClick={onClose}
              className={({ isActive }) => cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all group text-sm',
                isActive
                  ? 'bg-white text-black font-semibold'
                  : 'text-white/50 hover:text-white hover:bg-white/[0.07]'
              )}
            >
              {({ isActive }) => (
                <>
                  <BarChart3 className={cn('w-4 h-4', isActive ? 'text-black' : 'text-white/40 group-hover:text-white/70')} />
                  <span className="font-medium">Dashboard</span>
                </>
              )}
            </NavLink>
          </div>
        )}
      </nav>

      {/* Footer */}
      <div className="p-3 space-y-1 border-t border-white/[0.06]">
        {/* Crédits */}
        <NavLink
          to="/app/credits"
          onClick={onClose}
          className={({ isActive }) => cn(
            'flex items-center justify-between px-3 py-2.5 rounded-xl transition-all',
            isActive ? 'bg-white text-black' : 'text-white/50 hover:text-white hover:bg-white/[0.07]'
          )}
        >
          {({ isActive }) => (
            <>
              <div className="flex items-center gap-3">
                <Coins className={cn('w-4 h-4', isActive ? 'text-black' : 'text-amber-400')} />
                <span className="font-medium text-sm">Crédits</span>
              </div>
              {isLoadingCredits ? (
                <div className="w-8 h-4 rounded-full bg-white/10 animate-pulse" />
              ) : (
                <span className={cn(
                  'text-xs font-bold px-2 py-0.5 rounded-full flex items-center',
                  isActive ? 'bg-black/10 text-black' :
                  creditsBalance >= 999999 ? 'bg-emerald-500/20 text-emerald-400' :
                  creditsBalance < 5 ? 'bg-red-500/20 text-red-400' :
                  creditsBalance < 20 ? 'bg-amber-500/20 text-amber-400' :
                  'bg-emerald-500/20 text-emerald-400'
                )}>
                  {creditsBalance >= 999999 ? <Infinity className="w-3.5 h-3.5" /> : creditsBalance}
                </span>
              )}
            </>
          )}
        </NavLink>

        {/* Mon compte */}
        <NavLink
          to="/app/account"
          onClick={onClose}
          className={({ isActive }) => cn(
            'flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all text-sm',
            isActive ? 'bg-white text-black font-semibold' : 'text-white/50 hover:text-white hover:bg-white/[0.07]'
          )}
        >
          {({ isActive }) => (
            <>
              <Settings className={cn('w-4 h-4', isActive ? 'text-black' : 'text-white/40')} />
              <span className="font-medium">Mon compte</span>
            </>
          )}
        </NavLink>

        {/* Déconnexion */}
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-white/30 hover:text-red-400 hover:bg-red-500/10 transition-all text-sm"
        >
          <LogOut className="w-4 h-4" />
          <span className="font-medium">Déconnexion</span>
        </button>
      </div>
    </div>
  );
}